import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Mic, Square, Keyboard, Volume2, Loader2, RefreshCw } from 'lucide-react';
import Button from '@/components/ui/Button';
import { logger } from '@/lib/logger';
import { track, type WaitlistCtaSource } from '@/lib/analytics';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';
import {
  getSampleQuestion,
  checkSampleAnswer,
  transcribeSampleAudio,
  extractLandingErrorCode,
  type SampleQuestion,
  type SampleCheckResponse,
} from '@/lib/api/public';
import { getLandingError } from '@/lib/errors/landingErrors';
import type { FriendlyError } from '@/lib/errors/types';

const STORAGE_KEY = 'landing_progress';

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function persistProgress(answered: number, lastCorrect: boolean) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ answered, lastCorrect, ts: Date.now() })
    );
  } catch {
    // ignore quota / private mode failures
  }
}

interface InteractiveQuestionDemoProps {
  onOpenWaitlist: (source: WaitlistCtaSource) => void;
}

export default function InteractiveQuestionDemo({
  onOpenWaitlist,
}: InteractiveQuestionDemoProps) {
  const [question, setQuestion] = useState<SampleQuestion | null>(null);
  const [loadingQuestion, setLoadingQuestion] = useState(true);
  const [loadError, setLoadError] = useState<FriendlyError | null>(null);

  const [mode, setMode] = useState<'voice' | 'text'>('voice');
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<FriendlyError | null>(null);
  const [result, setResult] = useState<SampleCheckResponse | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [transcribing, setTranscribing] = useState(false);

  const {
    status: recorderStatus,
    durationMs,
    error: recorderError,
    startRecording,
    stopRecording,
    cancel: cancelRecording,
    clearError: clearRecorderError,
  } = useAudioRecorder();
  const {
    playQuestion: playQuestionAudio,
    isPlaying: isAudioPlaying,
    isLoading: isAudioLoading,
  } = useAudioPlayer();
  const isRecording = recorderStatus === 'recording';
  const voiceState: 'idle' | 'recording' | 'transcribing' = transcribing
    ? 'transcribing'
    : isRecording
      ? 'recording'
      : 'idle';

  // Recorder errors (mic permission, too-short clip) are surfaced through the
  // same banner as API errors. The hook owns that error state, so we just map
  // its current value into a FriendlyError when present and let API failures
  // (submitError) take over once recording succeeds.
  const voiceBanner: FriendlyError | null = recorderError
    ? getLandingError(recorderError)
    : submitError;

  useEffect(() => {
    logger.debug('[landing] InteractiveQuestionDemo mounted');
    void loadQuestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mode !== 'voice' && recorderStatus === 'recording') {
      cancelRecording();
    }
  }, [mode, recorderStatus, cancelRecording]);

  const loadQuestion = async () => {
    setLoadingQuestion(true);
    setLoadError(null);
    setResult(null);
    setSubmitError(null);
    clearRecorderError();
    setAnswer('');
    try {
      const q = await getSampleQuestion();
      setQuestion(q);
    } catch (err) {
      const code = extractLandingErrorCode(err, 'landing_load_failed');
      const friendly = getLandingError(code);
      logger.warn('[landing] question load error', { code });
      setLoadError(friendly);
    } finally {
      setLoadingQuestion(false);
    }
  };

  const submitAnswer = async (text: string) => {
    if (!question || !text || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await checkSampleAnswer({
        question_id: question.id,
        user_answer: text,
      });
      setResult(res);
      track('landing_demo_answer_graded', {
        question_id: question.id,
        input_mode: mode,
        is_correct: res.is_correct,
      });
      const nextAttempts = attempts + 1;
      setAttempts(nextAttempts);
      persistProgress(nextAttempts, res.is_correct);
    } catch (err) {
      const code = extractLandingErrorCode(err, 'landing_grade_failed');
      const friendly = getLandingError(code);
      logger.warn('[landing] grade error', { code });
      setSubmitError(friendly);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => submitAnswer(answer.trim());

  const handleMicPress = async () => {
    if (!question || submitting || transcribing) return;

    if (isRecording) {
      const blob = await stopRecording();
      // null → recorder set its own error (e.g. recording_too_short), already in voiceBanner.
      if (!blob) return;

      setTranscribing(true);
      setSubmitError(null);
      try {
        const { transcript } = await transcribeSampleAudio(blob);
        const trimmed = transcript.trim();
        if (!trimmed) {
          setSubmitError(getLandingError('TRANSCRIPTION_EMPTY_RESULT'));
          return;
        }
        setAnswer(trimmed);
        await submitAnswer(trimmed);
      } catch (err) {
        const code = extractLandingErrorCode(err, 'landing_transcribe_failed');
        const friendly = getLandingError(code);
        logger.warn('[landing] transcribe error', { code });
        setSubmitError(friendly);
      } finally {
        setTranscribing(false);
      }
      return;
    }

    // Starting a new recording — clear any prior banner so the user gets a
    // clean slate before the mic prompt appears.
    setSubmitError(null);
    clearRecorderError();
    await startRecording();
  };

  const openWaitlist = (reason: WaitlistCtaSource) => {
    logger.info('[landing] opening waitlist modal', { reason });
    onOpenWaitlist(reason);
  };

  return (
    <section id="try-it" className="relative bg-cream-warm py-24 md:py-32">
      <div className="mx-auto w-full max-w-3xl px-6 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-15%' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <header className="mb-12 md:mb-14">
            <p className="font-display font-light text-sm text-text-secondary mb-5 flex items-center gap-3">
              <span className="tabular-nums">01</span>
              <span className="w-8 h-px bg-text-muted/50" />
              <span>Try it</span>
            </p>
            <h2 className="font-display font-light text-text-primary text-4xl md:text-5xl leading-[1.1] tracking-[-0.01em] mb-4 max-w-[20ch]">
              One real question. Graded right now.
            </h2>
            <p className="font-sans text-base md:text-lg text-text-secondary max-w-[52ch] leading-relaxed">
              The same grader that runs inside the app. No sign-up, no email,
              no flashcard wrapper. Just your spoken answer, scored against
              the USCIS-accepted variants.
            </p>
          </header>

          <div className="relative rounded-3xl bg-card border-2 border-primary/20 p-6 md:p-10 shadow-[0_1px_2px_rgba(45,36,22,0.04),0_8px_32px_-12px_rgba(45,36,22,0.12)] min-h-[420px] flex flex-col">
          <span className="absolute -top-3 left-6 md:left-10 inline-flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-[0.2em] bg-primary text-white px-3 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            Live demo
          </span>
          {loadingQuestion ? (
            <QuestionSkeleton />
          ) : loadError ? (
            <ErrorState
              error={loadError}
              onRetry={loadQuestion}
              onSignUp={() => openWaitlist('load_error_signup')}
            />
          ) : question ? (
            <AnimatePresence mode="wait">
              {result ? (
                <motion.div
                  key={`result-${question.id}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35 }}
                >
                  <QuestionHeader
                    question={question}
                    attempts={attempts}
                    onPlayAudio={() => playQuestionAudio(question.id)}
                    isAudioPlaying={isAudioPlaying}
                    isAudioLoading={isAudioLoading}
                  />

                  <div className="rounded-xl border-2 border-border bg-cream-warm/40 p-4 mb-4">
                    <p className="font-sans text-xs font-semibold uppercase tracking-wider text-text-muted mb-1.5">
                      Your answer
                    </p>
                    <p className="font-display text-lg leading-snug text-text-primary">
                      &ldquo;{result.user_answer}&rdquo;
                    </p>
                  </div>

                  <div
                    className={`rounded-2xl border-2 p-5 mb-5 ${
                      result.is_correct
                        ? 'border-success bg-success/10'
                        : 'border-error bg-error/10'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">
                        {result.is_correct ? '🎉' : '💪'}
                      </span>
                      <p className="font-display font-semibold text-lg text-text-primary">
                        {result.is_correct
                          ? 'Correct!'
                          : 'Not quite. Keep going.'}
                      </p>
                    </div>
                    {!result.is_correct && (
                      <p className="font-sans text-sm text-text-secondary mb-2">
                        The accepted answer is &ldquo;{result.correct_answer}.&rdquo;
                      </p>
                    )}
                    {result.explanation && (
                      <p className="font-sans text-sm text-text-secondary">
                        {result.explanation}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={loadQuestion}
                      disabled={loadingQuestion}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-primary text-text-primary bg-transparent font-semibold font-sans text-base py-3 px-6 cursor-pointer disabled:opacity-50"
                    >
                      <RefreshCw size={16} />
                      Try another question
                    </button>
                    <Button onClick={() => openWaitlist('continue_after_result')}>
                      <span className="inline-flex items-center gap-2">
                        Notify me when it's ready
                        <ArrowRight size={18} />
                      </span>
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key={`question-${question.id}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35 }}
                >
                  <QuestionHeader
                    question={question}
                    attempts={attempts}
                    onPlayAudio={() => playQuestionAudio(question.id)}
                    isAudioPlaying={isAudioPlaying}
                    isAudioLoading={isAudioLoading}
                  />

                  <div className="flex justify-center mb-7">
                    <ModeToggle mode={mode} onChange={setMode} />
                  </div>

                  <AnimatePresence mode="wait">
                    {mode === 'voice' ? (
                      <motion.div
                        key="voice"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.25 }}
                        className="flex flex-col items-center pb-2"
                      >
                        <PulsingMic
                          state={voiceState}
                          disabled={submitting}
                          onPress={handleMicPress}
                        />
                        <p
                          className="font-sans text-sm text-text-secondary mt-6 text-center max-w-sm"
                          aria-live="polite"
                        >
                          {voiceState === 'recording'
                            ? `Recording ${formatDuration(durationMs)} — tap again to submit`
                            : voiceState === 'transcribing'
                              ? 'Transcribing your answer…'
                              : 'Tap and speak your answer'}
                        </p>
                        {voiceBanner && (
                          <div className="w-full mt-4">
                            <SubmitErrorBanner
                              error={voiceBanner}
                              onSignUp={() => openWaitlist('submit_error_signup')}
                            />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => setMode('text')}
                          className="mt-3 font-sans text-sm text-primary hover:text-primary-dark underline underline-offset-2 cursor-pointer"
                        >
                          Or try with text instead →
                        </button>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="text"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.25 }}
                        className="space-y-4"
                      >
                        <input
                          type="text"
                          value={answer}
                          onChange={(e) => setAnswer(e.target.value)}
                          onKeyDown={(e) =>
                            e.key === 'Enter' && handleSubmit()
                          }
                          placeholder="Type your answer..."
                          maxLength={1000}
                          disabled={submitting}
                          className="w-full px-4 py-3.5 rounded-xl bg-input-bg border-2 border-border text-base font-sans text-text-primary outline-none focus:border-primary transition-colors disabled:opacity-60"
                        />
                        <Button
                          fullWidth
                          onClick={handleSubmit}
                          disabled={!answer.trim()}
                          loading={submitting}
                        >
                          Submit answer
                        </Button>
                        {submitError && (
                          <SubmitErrorBanner
                            error={submitError}
                            onSignUp={() => openWaitlist('submit_error_signup')}
                          />
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
            </AnimatePresence>
          ) : null}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function QuestionHeader({
  question,
  attempts,
  onPlayAudio,
  isAudioPlaying,
  isAudioLoading,
}: {
  question: SampleQuestion;
  attempts: number;
  onPlayAudio: () => void;
  isAudioPlaying: boolean;
  isAudioLoading: boolean;
}) {
  return (
    <>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <span className="inline-block font-sans text-xs font-semibold uppercase tracking-[0.2em] bg-primary-light text-primary px-3 py-1 rounded-full">
          {question.category}
        </span>
        {attempts > 0 && (
          <span className="font-sans text-xs text-text-muted">
            {attempts} answered
          </span>
        )}
      </div>

      <div className="text-center mb-6">
        <h3 className="font-display font-semibold text-2xl md:text-3xl text-text-primary leading-snug mb-5">
          {question.question_text}
        </h3>
        <button
          type="button"
          onClick={onPlayAudio}
          disabled={isAudioPlaying || isAudioLoading}
          aria-label="Play question"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-card/60 hover:bg-card font-sans text-sm font-medium text-primary hover:text-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          {isAudioLoading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Volume2 size={16} />
          )}
          {isAudioPlaying ? 'Playing…' : isAudioLoading ? 'Loading…' : 'Play question'}
        </button>
      </div>
    </>
  );
}

function QuestionSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading question"
      className="animate-pulse flex-1 flex flex-col"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="h-6 w-44 bg-cream-warm rounded-full" />
      </div>
      <div className="text-center space-y-3 mb-7">
        <div className="h-7 md:h-9 w-3/4 bg-cream-warm rounded-md mx-auto" />
        <div className="h-7 md:h-9 w-1/2 bg-cream-warm rounded-md mx-auto" />
      </div>
      <div className="h-9 w-32 bg-cream-warm rounded-full mx-auto mb-8" />
      <div className="h-12 w-full bg-cream-warm rounded-xl mt-auto" />
    </div>
  );
}

function ErrorState({
  error,
  onRetry,
  onSignUp,
}: {
  error: FriendlyError;
  onRetry: () => void;
  onSignUp: () => void;
}) {
  const showSignUp = error.category === 'rate_limit';
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center py-6">
      <h3 className="font-display font-semibold text-xl text-text-primary mb-2">
        {error.title}
      </h3>
      <p className="font-sans text-sm text-text-secondary max-w-md mb-6">
        {error.message} {error.action}
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        {error.retryable && (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-primary text-text-primary bg-transparent font-semibold font-sans text-base py-3 px-6 cursor-pointer"
          >
            <RefreshCw size={16} />
            Try again
          </button>
        )}
        {showSignUp && (
          <Button onClick={onSignUp}>
            <span className="inline-flex items-center gap-2">
              Notify me instead
              <ArrowRight size={18} />
            </span>
          </Button>
        )}
      </div>
    </div>
  );
}

function SubmitErrorBanner({
  error,
  onSignUp,
}: {
  error: FriendlyError;
  onSignUp: () => void;
}) {
  const showSignUp = error.category === 'rate_limit';
  return (
    <div className="rounded-xl border-2 border-error/30 bg-error-light p-4 text-left">
      <p className="font-display font-semibold text-base text-error leading-tight mb-1">
        {error.title}
      </p>
      <p className="font-sans text-sm text-error/85 mb-3">
        {error.message} {error.action}
      </p>
      {showSignUp && (
        <button
          type="button"
          onClick={onSignUp}
          className="inline-flex items-center gap-1.5 font-sans font-semibold text-sm text-error hover:text-error/80 cursor-pointer"
        >
          Notify me
          <ArrowRight size={14} />
        </button>
      )}
    </div>
  );
}

function ModeToggle({
  mode,
  onChange,
}: {
  mode: 'voice' | 'text';
  onChange: (m: 'voice' | 'text') => void;
}) {
  const options: Array<{
    value: 'voice' | 'text';
    label: string;
    Icon: typeof Mic;
  }> = [
    { value: 'voice', label: 'Voice', Icon: Mic },
    { value: 'text', label: 'Text', Icon: Keyboard },
  ];

  return (
    <div
      role="tablist"
      aria-label="Answer input mode"
      className="inline-flex items-stretch self-center gap-0 border-b border-border/70"
    >
      {options.map(({ value, label, Icon }, idx) => {
        const active = mode === value;
        return (
          <button
            key={value}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(value)}
            className={`relative inline-flex items-center gap-2 px-6 py-3 font-sans text-sm transition-colors cursor-pointer ${
              idx > 0 ? 'border-l border-border/70' : ''
            } ${
              active
                ? 'text-primary font-semibold'
                : 'text-text-muted hover:text-text-secondary font-medium'
            }`}
          >
            <Icon size={15} />
            <span>{label}</span>
            {active && (
              <motion.span
                layoutId="landing-mode-underline"
                className="absolute -bottom-[1px] left-3 right-3 h-[2px] bg-primary rounded-full"
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

function PulsingMic({
  state,
  disabled,
  onPress,
}: {
  state: 'idle' | 'recording' | 'transcribing';
  disabled: boolean;
  onPress: () => void;
}) {
  const ringTransition = {
    duration: 2.4,
    ease: 'easeOut' as const,
    repeat: Infinity,
    times: [0, 0.4, 1],
  };

  const ariaLabel =
    state === 'recording'
      ? 'Stop recording'
      : state === 'transcribing'
        ? 'Transcribing your answer'
        : 'Start recording';

  const buttonClasses =
    state === 'recording'
      ? 'bg-error'
      : 'bg-gradient-to-br from-primary to-primary-dark';

  return (
    <div className="relative w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
      {state === 'idle' && (
        <>
          <motion.span
            aria-hidden
            animate={{ scale: [1, 1.2, 1.55], opacity: [0, 0.45, 0] }}
            transition={ringTransition}
            className="absolute inset-0 rounded-full border-2 border-primary pointer-events-none"
          />
          <motion.span
            aria-hidden
            animate={{ scale: [1, 1.2, 1.55], opacity: [0, 0.35, 0] }}
            transition={{ ...ringTransition, delay: 1.2 }}
            className="absolute inset-0 rounded-full border-2 border-primary pointer-events-none"
          />
        </>
      )}
      {state === 'recording' && (
        <motion.span
          aria-hidden
          animate={{ scale: [1, 1.25, 1.5], opacity: [0.5, 0.25, 0] }}
          transition={{ duration: 1.4, ease: 'easeOut', repeat: Infinity }}
          className="absolute inset-0 rounded-full border-2 border-error pointer-events-none"
        />
      )}

      <motion.button
        type="button"
        onClick={onPress}
        disabled={disabled || state === 'transcribing'}
        aria-label={ariaLabel}
        whileTap={!disabled && state !== 'transcribing' ? { scale: 0.94 } : undefined}
        animate={state === 'idle' ? { scale: [1, 1.04, 1] } : { scale: 1 }}
        transition={
          state === 'idle'
            ? { duration: 2.4, ease: 'easeInOut', repeat: Infinity }
            : { duration: 0.2 }
        }
        className={`relative w-full h-full rounded-full ${buttonClasses} flex items-center justify-center shadow-lg ${
          disabled || state === 'transcribing'
            ? 'opacity-60 cursor-not-allowed'
            : 'cursor-pointer'
        }`}
      >
        {state === 'transcribing' ? (
          <span className="w-7 h-7 border-3 border-white border-t-transparent rounded-full animate-spin" />
        ) : state === 'recording' ? (
          <Square size={28} className="text-white" fill="currentColor" />
        ) : (
          <Mic size={32} className="text-white" />
        )}
      </motion.button>
    </div>
  );
}

